import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  Breadcrumbs,
  Link,
  TextField,
  Button,
  Grid,
  Paper,
  MenuItem,
  CircularProgress,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from "@mui/material";
import {
  NavigateNext as NavigateNextIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  LiveHelp as LiveHelpIcon,
  Payment as PaymentIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getData, postData, postFileUpload } from "../../Webservice/Webservice";
import { getToken } from "../../Utils/authHelper";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

const THEME_COLOR = "#C8A054";

const HELP_TOPICS = [
  { id: "orders", title: "My Orders", icon: <ShoppingCartIcon />, description: "Track, cancel or return an order" },
  { id: "returns", title: "Returns & Refunds", icon: <HistoryIcon />, description: "Check status of your refunds" },
  { id: "payment", title: "Payments & Rewards", icon: <PaymentIcon />, description: "Payment failures & wallet issues" },
  { id: "account", title: "Account & Profile", icon: <PersonIcon />, description: "Login, security & address issues" },
  { id: "general", title: "General Inquiry", icon: <LiveHelpIcon />, description: "Other questions about our services" },
];

const FAQ_DATA = {
  orders: [
    { q: "How do I track my order?", a: "You can track your order by visiting the 'My Orders' section in your profile. Click on the specific order to see real-time tracking information and the expected delivery date." },
    { q: "Can I cancel my order?", a: "Orders can be cancelled before they are shipped. Go to 'My Orders', select the order, and look for the 'Cancel Order' button. If the order is already shipped, you may need to initiate a return after delivery." },
    { q: "Why is my order delayed?", a: "Delays can occur due to high demand, weather conditions, or courier issues. Check your tracking link for updates. If there's no update for 48 hours, please contact us." },
  ],
  returns: [
    { q: "How do I return a product?", a: "You can initiate a return within 7 days of delivery. Go to 'My Orders', select the delivered product, and click 'Return Item'. Choose the reason and follow the instructions." },
    { q: "When will I get my refund?", a: "Once the product is picked up and reaches our warehouse, we process the refund within 2-3 business days. The amount will be credited back to your original payment method." },
    { q: "What is the return policy?", a: "We offer a 7-day return policy for most items. The product must be unused, in its original packaging, and with all tags intact." },
  ],
  payment: [
    { q: "My payment failed but money was deducted.", a: "Don't worry! If a payment fails but money is debited, it usually gets refunded automatically within 5-7 business days by your bank. Check your statement for a reversal." },
    { q: "What payment methods do you accept?", a: "We accept all major Credit/Debit cards, UPI (GPay, PhonePe, etc.), Net Banking, and popular Wallets." },
    { q: "Is Cash on Delivery available?", a: "Yes, COD is available for most locations. You can check the availability on the product page by entering your PIN code." },
  ],
  account: [
    { q: "How do I change my mobile number?", a: "Go to Profile > Account Settings > Login & Security. Click 'Change' next to your mobile number and verify with the OTP sent to your new number." },
    { q: "I forgot my password.", a: "Click on 'Show/Hide' and then 'Forgot Password' on the login page. We will send a reset link to your registered email address." },
    { q: "How do I delete my account?", a: "To delete your account, please send a request to glamcare@glamcompany.in from your registered email address." },
  ],
  general: [
    { q: "How do I contact customer support?", a: "You can reach us via the 'Contact Us' section, email us at glamcare@glamcompany.in, or call our helpline at +91 9600602093." },
    { q: "Do you ship internationally?", a: "Currently, we only ship within India. We are working on expanding our services to other countries soon." },
    { q: "Where is my nearest store?", a: "TVFM is primarily an online platform, ensuring you get the best products delivered right to your doorstep." },
  ]
};

function Support() {
  const navigate = useNavigate();
  const [view, setView] = useState("dashboard"); // dashboard, category, create-ticket, history
  const [selectedTopic, setSelectedTopic] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [fetchingTickets, setFetchingTickets] = useState(false);

  const rawToken = getToken();
  const token = rawToken ? `Bearer ${rawToken}` : "";

  useEffect(() => {
    if (rawToken) {
      fetchOrders();
      fetchTickets();
    }
  }, [rawToken]);

  // Mandatory scroll to top on view changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  const fetchOrders = async () => {
    try {
      const res = await postData("order/order-list", {}, token, false, false);
      if (res && res.orders) {
        setOrders(res.orders);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  const fetchTickets = async () => {
    setFetchingTickets(true);
    try {
      const res = await getData("query/list", null, token, false);
      if (res && res.queries) {
        setTickets(res.queries);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setFetchingTickets(false);
    }
  };

  const handleTopicClick = (topic) => {
    setSelectedTopic(topic);
    setView("category");
    window.scrollTo(0, 0);
  };

  const handleCreateTicketClick = () => {
    if (selectedTopic) {
      setSubject(selectedTopic.title);
    }
    setView("create-ticket");
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 5) {
      alert("You can only upload up to 5 images.");
      return;
    }
    setImages((prev) => [...prev, ...files]);
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setPreviewImages((prev) => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      navigate("/signin");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("subject", subject);
      formData.append("description", description);
      if (selectedOrder) formData.append("orderId", selectedOrder);
      images.forEach((image) => formData.append("images", image));

      const res = await postFileUpload("query/add", formData, token);
      if (res) {
        setSubject("");
        setDescription("");
        setSelectedOrder("");
        setImages([]);
        setPreviewImages([]);
        fetchTickets();
        setView("history");
      }
    } catch (error) {
      console.error("Error creating ticket:", error);
    } finally {
      setLoading(false);
    }
  };

  // Render logic for different views
  const renderDashboard = () => (
    <Box>
      {/* Hero Section */}
      <Box 
        sx={{ 
          background: "linear-gradient(135deg, #C8A054 0%, #a67c32 100%)",
          borderRadius: { xs: "0", md: "24px" },
          p: { xs: 4, md: 8 },
          color: "#fff",
          textAlign: "center",
          mb: 6
        }}
      >
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, ...rubikfont, fontSize: { xs: "28px", md: "42px" } }}>
          How can we help you?
        </Typography>
        <Typography sx={{ mb: 4, opacity: 0.9, fontSize: "16px" }}>
          Explore our help topics or track your existing support tickets
        </Typography>
        <Box sx={{ maxWidth: "600px", mx: "auto", position: "relative" }}>
          <TextField 
            fullWidth 
            placeholder="Search for help topics, orders, or tickets..."
            sx={{ 
              bgcolor: "#fff", 
              borderRadius: "50px",
              "& .MuiOutlinedInput-root": { borderRadius: "50px", px: 2 },
              "& .MuiOutlinedInput-notchedOutline": { border: "none" }
            }}
          />
        </Box>
      </Box>

      {/* Quick Actions / Active Tickets */}
      {tickets.filter(t => t.status !== "resolved").length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, ...rubikfont }}>Your Recent Conversations</Typography>
          <Grid container spacing={3}>
            {tickets.filter(t => t.status !== "resolved").slice(0, 2).map(ticket => (
              <Grid item xs={12} md={6} key={ticket._id}>
                <Paper 
                  elevation={0} 
                  onClick={() => navigate(`/support/ticket/${ticket._id}`)}
                  sx={{ 
                    p: 3, 
                    borderRadius: "16px", 
                    border: "1px solid #E9ECEF", 
                    cursor: "pointer",
                    transition: "0.3s",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    "&:hover": { boxShadow: "0 10px 30px rgba(0,0,0,0.05)", borderColor: THEME_COLOR }
                  }}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                       <Box sx={{ px: 1.5, py: 0.3, borderRadius: "4px", bgcolor: "#FFF3E0", color: "#F59E0B", fontSize: "10px", fontWeight: 800 }}>{ticket.status.toUpperCase()}</Box>
                       <Typography sx={{ fontSize: "12px", color: "#999" }}>#{ticket.referenceNo}</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "16px", ...rubikfont }}>{ticket.subject}</Typography>
                    <Typography sx={{ fontSize: "13px", color: "#666", mt: 0.5 }}>Last update: {new Date(ticket.updatedAt).toLocaleDateString()}</Typography>
                  </Box>
                  <NavigateNextIcon sx={{ color: THEME_COLOR }} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Main Categories */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, ...rubikfont }}>Explore Help Topics</Typography>
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {HELP_TOPICS.map((topic) => (
          <Grid item xs={12} sm={6} md={3} key={topic.id}>
            <Paper
              elevation={0}
              onClick={() => {
                setSelectedTopic(topic);
                setView("category");
              }}
              sx={{
                p: 4,
                textAlign: "center",
                borderRadius: "20px",
                border: "1px solid #E9ECEF",
                cursor: "pointer",
                transition: "0.3s",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.05)",
                  borderColor: THEME_COLOR,
                  "& .icon-box": { bgcolor: THEME_COLOR, color: "#fff" }
                },
              }}
            >
              <Box className="icon-box" sx={{ width: 60, height: 60, borderRadius: "15px", bgcolor: "#f8f9fa", color: THEME_COLOR, display: "flex", alignItems: "center", justifyContent: "center", mb: 2, transition: "0.3s" }}>
                {topic.icon}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: "16px" }}>{topic.title}</Typography>
              <Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>{topic.desc}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Footer Actions */}
      <Box sx={{ p: 4, borderRadius: "24px", bgcolor: "#F8F9FA", textAlign: "center", border: "1px dashed #DDD" }}>
         <Typography sx={{ fontWeight: 700, mb: 1 }}>Didn't find what you were looking for?</Typography>
         <Typography sx={{ fontSize: "14px", color: "#666", mb: 3 }}>Our support specialists are ready to assist you with any query.</Typography>
         <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <Button 
                variant="contained" 
                onClick={() => setView("create-ticket")}
                sx={{ bgcolor: THEME_COLOR, borderRadius: "50px", px: 4, py: 1.2, fontWeight: 700, boxShadow: "none", "&:hover": { bgcolor: "#b08a46" } }}
            >
                Raise a Support Ticket
            </Button>
            <Button 
                variant="outlined" 
                onClick={() => setView("history")}
                sx={{ borderRadius: "50px", px: 4, py: 1.2, fontWeight: 700, borderColor: "#ddd", color: "#333", "&:hover": { borderColor: THEME_COLOR, color: THEME_COLOR } }}
            >
                View Ticket History
            </Button>
         </Box>
      </Box>
    </Box>
  );

  const renderCategoryFAQs = () => (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <IconButton onClick={() => setView("dashboard")} sx={{ bgcolor: "#eee", "&:hover": { bgcolor: "#ddd" } }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, ...rubikfont }}>{selectedTopic?.title}</Typography>
            <Typography sx={{ color: "#666" }}>Everything you need to know about {selectedTopic?.title.toLowerCase()}</Typography>
        </Box>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Frequently Asked Questions</Typography>
          {FAQ_DATA[selectedTopic?.id || "general"].map((faq, index) => (
            <Accordion key={index} sx={{ mb: 1, borderRadius: "12px !important", "&:before": { display: "none" }, boxShadow: "none", border: "1px solid #E9ECEF" }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600, fontSize: "15px" }}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ fontSize: "14px", color: "#444", lineHeight: 1.6 }}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
        <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", bgcolor: THEME_COLOR, color: "#fff" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Still need help?</Typography>
                <Typography sx={{ fontSize: "14px", mb: 3, opacity: 0.9 }}>If these FAQs didn't resolve your issue, please create a ticket and we'll get back to you shortly.</Typography>
                <Button 
                    fullWidth 
                    variant="contained" 
                    onClick={() => setView("create-ticket")}
                    sx={{ bgcolor: "#fff", color: THEME_COLOR, fontWeight: 800, borderRadius: "10px", "&:hover": { bgcolor: "#f0f0f0" } }}
                >
                    Create Ticket
                </Button>
            </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderCreateTicket = () => (
    <Box>
       <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <IconButton onClick={() => setView("dashboard")} sx={{ bgcolor: "#eee" }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        <Typography variant="h4" sx={{ fontWeight: 800, ...rubikfont }}>Create Support Ticket</Typography>
      </Box>

      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: "24px", border: "1px solid #E9ECEF" }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Stack spacing={3}>
                <Box>
                  <Typography sx={{ fontWeight: 700, mb: 1, fontSize: "14px" }}>Related Order (Optional)</Typography>
                  <TextField
                    select
                    fullWidth
                    value={selectedOrder}
                    onChange={(e) => setSelectedOrder(e.target.value)}
                    placeholder="Select your order"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  >
                    <MenuItem value="">General Inquiry (No Order)</MenuItem>
                    {orders.map((order) => (
                      <MenuItem key={order._id} value={order._id}>
                        Order #{order.orderNumber} - {new Date(order.createdAt).toLocaleDateString()}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, mb: 1, fontSize: "14px" }}>Issue Subject</Typography>
                  <TextField 
                    fullWidth 
                    required 
                    placeholder="Briefly describe the issue (e.g., Damaged item received)" 
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, mb: 1, fontSize: "14px" }}>Detailed Description</Typography>
                  <TextField 
                    fullWidth 
                    required 
                    multiline 
                    rows={6} 
                    placeholder="Please provide as much detail as possible so we can help you faster..." 
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
                <Box sx={{ p: 3, bgcolor: "#F8F9FA", borderRadius: "16px", border: "1px dashed #DDD" }}>
                    <Typography sx={{ fontWeight: 700, mb: 2, fontSize: "14px", display: "flex", alignItems: "center", gap: 1 }}>
                        <CloudUploadIcon color="primary" /> Attach Images (Max 5)
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#666", mb: 2 }}>Adding photos of the product, packaging, or labels helps us resolve your issue much faster.</Typography>
                    
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
                        {previewImages.map((src, i) => (
                            <Box key={i} sx={{ position: "relative", width: 80, height: 80 }}>
                                <img src={src} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "8px" }} />
                                <IconButton size="small" onClick={() => removeImage(i)} sx={{ position: "absolute", top: -8, right: -8, bgcolor: "#fff", boxShadow: "0 2px 5px rgba(0,0,0,0.1)", p: 0.2 }}>
                                    <CloseIcon sx={{ fontSize: 13, color: "red" }} />
                                </IconButton>
                            </Box>
                        ))}
                        {previewImages.length < 5 && (
                            <Box 
                                component="label" 
                                sx={{ 
                                    width: 80, height: 80, borderRadius: "8px", border: "2px dashed #CCC", 
                                    display: "flex", alignItems: "center", justifyContent: "center", 
                                    cursor: "pointer", "&:hover": { borderColor: THEME_COLOR, bgcolor: "#fff" } 
                                }}
                            >
                                <CloudUploadIcon sx={{ color: "#AAA" }} />
                                <input type="file" hidden multiple accept="image/*" onChange={handleImageChange} />
                            </Box>
                        )}
                    </Box>
                </Box>

                <Button 
                    fullWidth 
                    type="submit" 
                    disabled={loading}
                    sx={{ 
                        mt: 4, bgcolor: THEME_COLOR, color: "#fff", borderRadius: "12px", py: 2, fontWeight: 800, fontSize: "16px",
                        boxShadow: "0 10px 20px " + THEME_COLOR + "44",
                        "&:hover": { bgcolor: "#b08a46", boxShadow: "none" }
                    }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Submit Support Request"}
                </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );

  const renderHistory = () => (
    <Box>
       <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <IconButton onClick={() => setView("dashboard")} sx={{ bgcolor: "#eee" }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        <Typography variant="h4" sx={{ fontWeight: 800, ...rubikfont }}>Ticket History</Typography>
      </Box>

      {fetchingTickets ? (
        <Box sx={{ py: 10, textAlign: "center" }}>
            <CircularProgress color="primary" />
            <Typography sx={{ mt: 2 }}>Loading your tickets...</Typography>
        </Box>
      ) : tickets.length === 0 ? (
        <Paper sx={{ py: 10, textAlign: "center", borderRadius: "24px" }}>
            <HistoryIcon sx={{ fontSize: 60, color: "#DDD", mb: 2 }} />
            <Typography variant="h6">No tickets found</Typography>
            <Button sx={{ mt: 2, color: THEME_COLOR }} onClick={() => setView("create-ticket")}>Create your first ticket</Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
            {tickets.map((ticket) => (
                <Grid item xs={12} key={ticket._id}>
                    <Paper 
                        elevation={0} 
                        onClick={() => navigate(`/support/ticket/${ticket._id}`)}
                        sx={{ 
                            p: 3, borderRadius: "16px", border: "1px solid #E9ECEF", 
                            display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center",
                            cursor: "pointer", transition: "0.2s", "&:hover": { borderColor: THEME_COLOR, bgcolor: "#fdfdfd" }
                        }}
                    >
                        <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
                            <Box sx={{ width: 48, height: 48, borderRadius: "12px", bgcolor: "#f8f9fa", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <LiveHelpIcon sx={{ color: THEME_COLOR }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: "16px" }}>{ticket.subject}</Typography>
                                <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                                    <Typography sx={{ fontSize: "12px", color: "#999" }}>#{ticket.referenceNo}</Typography>
                                    <Typography sx={{ fontSize: "12px", color: "#999" }}>Created: {new Date(ticket.createdAt).toLocaleDateString()}</Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <Box sx={{ px: 2, py: 0.5, borderRadius: "20px", fontSize: "12px", fontWeight: 800, textTransform: "uppercase", bgcolor: ticket.status === "open" ? "#E3F2FD" : ticket.status === "in-progress" ? "#FFF3E0" : "#E8F5E9", color: ticket.status === "open" ? "#2196F3" : ticket.status === "in-progress" ? "#F59E0B" : "#2B8A3E" }}>
                                {ticket.status}
                            </Box>
                            <NavigateNextIcon sx={{ color: "#CCC" }} />
                        </Box>
                    </Paper>
                </Grid>
            ))}
        </Grid>
      )}
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh", pb: 10 }}>
        {/* Sub-header or subtle line */}
        <Box sx={{ borderBottom: "1px solid #eee", py: 2, mb: 0 }}>
            <Container maxWidth="lg">
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" sx={{ opacity: 0.5 }} />}>
                    <Link underline="hover" color="inherit" onClick={() => navigate("/")} sx={{ cursor: "pointer", fontSize: "13px" }}>HOME</Link>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>HELP CENTER</Typography>
                </Breadcrumbs>
            </Container>
        </Box>

        <Container maxWidth="lg" sx={{ pt: 4 }}>
            {view === "dashboard" && renderDashboard()}
            {view === "category" && renderCategoryFAQs()}
            {view === "create-ticket" && renderCreateTicket()}
            {view === "history" && renderHistory()}
        </Container>
    </Box>
  );
}

export default Support;
